// Protein calculator: pure engine (compute, buildDay) + DOM binding.
// All coefficients, portions and messages come from the section's JSON config,
// so the engine can be reused as-is by other calculators and tested under Node.
(function (root) {
  'use strict';

  function roundTo(value, step) {
    return Math.round(value / step) * step;
  }

  function toNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    var n = Number(String(value).replace(',', '.'));
    return isFinite(n) ? n : null;
  }

  function validate(input, limits) {
    var errors = {};
    var weight = toNumber(input.weight);
    var age = toNumber(input.age);
    var height = toNumber(input.height);

    if (weight === null || weight < limits.weight_min || weight > limits.weight_max) errors.weight = true;
    if (age === null || age < limits.age_min || age > limits.age_max) errors.age = true;
    if (height !== null && (height < limits.height_min || height > limits.height_max)) errors.height = true;
    if (!input.profile) errors.profile = true;

    return { errors: errors, weight: weight, age: age, height: height };
  }

  function buildDay(target, portions, diet, baseline) {
    var allowed = portions.filter(function (p) {
      if (diet === 'vegan') return p.diet === 'vegan';
      if (diet === 'veggie') return p.diet !== 'omni';
      return true;
    });
    var counts = allowed.map(function () { return 0; });
    var remaining = target - baseline;
    var added = true;

    // Reason: round-robin keeps the example day varied instead of stacking one food.
    while (remaining > 0 && added) {
      added = false;
      for (var i = 0; i < allowed.length && remaining > 0; i++) {
        if (counts[i] < allowed[i].max) {
          counts[i] += 1;
          remaining -= allowed[i].protein;
          added = true;
        }
      }
    }

    var items = [];
    var total = baseline;
    allowed.forEach(function (p, i) {
      if (!counts[i]) return;
      items.push({ label: p.label, amount: p.amount, count: counts[i], protein: p.protein * counts[i] });
      total += p.protein * counts[i];
    });

    return { items: items, baseline: baseline, total: total, reached: remaining <= 0 };
  }

  function compute(input, config) {
    var checked = validate(input, config.limits);
    if (Object.keys(checked.errors).length) return { status: 'invalid', errors: checked.errors };

    if (checked.age < config.adult_age) return { status: 'blocked', reason: 'minor' };
    if (input.situation && input.situation !== 'none') return { status: 'blocked', reason: input.situation };

    var profile = null;
    for (var i = 0; i < config.profiles.length; i++) {
      if (config.profiles[i].id === input.profile) profile = config.profiles[i];
    }
    if (!profile) return { status: 'invalid', errors: { profile: true } };

    var refWeight = checked.weight;
    var adjusted = false;
    if (checked.height) {
      var meters = checked.height / 100;
      var bmi = checked.weight / (meters * meters);
      if (bmi >= config.bmi_threshold) {
        refWeight = Math.round(config.bmi_reference * meters * meters);
        adjusted = true;
      }
    }

    var min = profile.min;
    var max = profile.max;
    var senior = checked.age >= config.senior.age;
    if (senior) {
      min = Math.max(min, config.senior.min);
      max = Math.max(max, config.senior.max);
    }
    if (input.diet === 'vegan') {
      min *= 1 + config.vegan_bonus / 100;
      max *= 1 + config.vegan_bonus / 100;
    }

    var floor = roundTo(refWeight * min, 5);
    var target = Math.max(roundTo(refWeight * max, 5), floor);
    var meals = toNumber(input.meals) || 3;

    return {
      status: 'ok',
      floor: floor,
      target: target,
      perMeal: roundTo(target / meals, 5),
      meals: meals,
      refWeight: refWeight,
      adjusted: adjusted,
      senior: senior,
      profile: profile,
      day: buildDay(target, config.portions, input.diet, config.baseline)
    };
  }

  function ageBand(age, config) {
    if (age >= config.senior.age) return config.senior.age + '+';
    if (age >= 40) return '40-' + (config.senior.age - 1);
    return config.adult_age + '-39';
  }

  function format(template, values) {
    return String(template).replace(/\{(\w+)\}/g, function (match, key) {
      return values[key] !== undefined ? values[key] : match;
    });
  }

  function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function renderDay(day, config) {
    var html = '<ul class="pc-day">';
    day.items.forEach(function (item) {
      var qty = item.count > 1 ? item.count + ' × ' : '';
      html += '<li><span class="pc-day-food">' + qty + escapeHtml(item.label) +
        (item.amount ? ' <small>(' + escapeHtml(item.amount) + ')</small>' : '') +
        '</span><span class="pc-day-g">≈ ' + item.protein + ' g</span></li>';
    });
    html += '<li class="pc-day-base"><span class="pc-day-food">' + escapeHtml(config.messages.baseline) +
      '</span><span class="pc-day-g">≈ ' + day.baseline + ' g</span></li>';
    html += '</ul>';
    if (!day.reached) {
      html += '<p class="pc-day-short">' + escapeHtml(format(config.messages.not_reached, { g: day.total })) + '</p>';
    }
    return html;
  }

  // Reason: Cowlendar adds the booking to the cart then shows its own confirmation; send the visitor straight to checkout instead.
  function checkoutAfterBooking() {
    if (checkoutAfterBooking.on || typeof PerformanceObserver === 'undefined') return;
    checkoutAfterBooking.on = true;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    try {
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (entry) {
          if (!/\/cart\/add(\.js)?(\?|$)/.test(entry.name) || entry.name.indexOf('cow_upsell') !== -1) return;
          window.fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
            .then(function (res) { return res.json(); })
            .then(function (cart) { if (cart.item_count > 0) window.location.href = root + 'checkout'; })
            .catch(function () {});
        });
      }).observe({ type: 'resource' });
    } catch (e) {}
  }

  function saveAttributes(attributes) {
    if (!window.fetch) return Promise.resolve();
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return window.fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    });
  }

  function init(el) {
    var configNode = el.querySelector('[data-pc-config]');
    var form = el.querySelector('[data-pc-form]');
    var output = el.querySelector('[data-pc-output]');
    if (!configNode || !form || !output) return;

    var config;
    try {
      config = JSON.parse(configNode.textContent);
    } catch (e) {
      return;
    }

    var refs = {
      placeholder: el.querySelector('[data-pc-placeholder]'),
      ok: output.querySelector('[data-pc-ok]'),
      floor: output.querySelector('[data-pc-floor]'),
      target: output.querySelector('[data-pc-target]'),
      perMeal: output.querySelector('[data-pc-per-meal]'),
      notes: output.querySelector('[data-pc-notes]'),
      day: output.querySelector('[data-pc-day]'),
      blocked: output.querySelectorAll('[data-pc-blocked]'),
      loading: output.querySelector('[data-pc-loading]'),
      callMessage: output.querySelector('[data-pc-call-message]')
    };
    var last = null;
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var submitted = false;
    var run = 0;

    function read() {
      var data = new FormData(form);
      return {
        weight: data.get('weight'),
        age: data.get('age'),
        height: data.get('height'),
        profile: data.get('profile'),
        diet: data.get('diet') || 'omni',
        situation: data.get('situation') || 'none',
        meals: data.get('meals')
      };
    }

    function showErrors(errors) {
      ['weight', 'age', 'height', 'profile'].forEach(function (name) {
        var field = form.querySelector('[data-pc-field="' + name + '"]');
        if (!field) return;
        var control = field.querySelector('input, select');
        var invalid = !!errors[name];
        field.classList.toggle('is-invalid', invalid);
        if (control) {
          if (invalid) control.setAttribute('aria-invalid', 'true');
          else control.removeAttribute('aria-invalid');
        }
      });
    }

    function render(result, input) {
      showErrors(result.errors || {});
      if (result.status === 'invalid') return false;

      output.hidden = false;
      if (refs.placeholder) refs.placeholder.hidden = true;
      Array.prototype.forEach.call(refs.blocked, function (node) {
        node.hidden = result.status !== 'blocked' || node.getAttribute('data-pc-blocked') !== result.reason;
      });
      refs.ok.hidden = result.status !== 'ok';
      last = result.status === 'ok' ? { result: result, input: input } : null;
      if (result.status !== 'ok') return true;

      refs.floor.textContent = result.floor;
      refs.target.textContent = result.target;
      refs.perMeal.textContent = format(config.messages.per_meal, { g: result.perMeal, meals: result.meals });

      var notes = [];
      if (result.adjusted) notes.push(format(config.messages.adjusted, { ref: result.refWeight }));
      if (result.senior) notes.push(format(config.messages.senior, { age: config.senior.age }));
      if (input.diet === 'vegan') notes.push(format(config.messages.vegan, { pct: config.vegan_bonus }));
      refs.notes.innerHTML = notes.map(function (n) { return '<p>' + escapeHtml(n) + '</p>'; }).join('');
      refs.notes.hidden = !notes.length;
      refs.day.innerHTML = renderDay(result.day, config);
      return true;
    }

    function publish(result, input) {
      var analytics = window.Shopify && window.Shopify.analytics;
      if (!analytics || typeof analytics.publish !== 'function') return;
      try {
        analytics.publish('protein_calc_submit', {
          status: result.status,
          reason: result.reason || null,
          goal: input.profile,
          diet: input.diet,
          age_band: result.status === 'ok' ? ageBand(toNumber(input.age), config) : null
        });
      } catch (e) {}
    }

    function scrollToOutput() {
      var header = document.querySelector('.shopify-section-group-header-group');
      var top = (header ? header.getBoundingClientRect().bottom : 0) + 16;
      var rect = output.getBoundingClientRect();
      if (rect.top >= top && rect.top <= window.innerHeight * 0.4) return;
      window.scrollBy({ top: rect.top - top, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    // Reason: a short "showing the work" beat (2.2 s in all) makes the figure feel computed, not looked up.
    function showLoading(id, done) {
      var steps = refs.loading ? refs.loading.querySelectorAll('[data-pc-step]') : [];
      if (!steps.length) return done();
      var settleMs = reduceMotion ? 0 : 320;
      var stepMs = reduceMotion ? 0 : (2200 - settleMs) / steps.length;
      output.hidden = false;
      output.classList.remove('is-in');
      output.setAttribute('aria-busy', 'true');
      if (refs.placeholder) refs.placeholder.hidden = true;
      [refs.ok].concat(Array.prototype.slice.call(refs.blocked)).forEach(function (node) { node.hidden = true; });
      Array.prototype.forEach.call(steps, function (step) { step.classList.remove('is-done'); });
      refs.loading.style.setProperty('--pc-load', (steps.length * stepMs) + 'ms');
      refs.loading.hidden = false;
      output.focus({ preventScroll: true });
      scrollToOutput();
      var i = 0;
      (function tick() {
        if (id !== run) return;
        if (i < steps.length) {
          window.setTimeout(function () {
            if (id !== run) return;
            steps[i++].classList.add('is-done');
            tick();
          }, stepMs);
          return;
        }
        window.setTimeout(function () {
          if (id !== run) return;
          refs.loading.hidden = true;
          output.removeAttribute('aria-busy');
          done();
        }, settleMs);
      })();
    }

    function reveal(field) {
      var vv = window.visualViewport;
      var header = document.querySelector('.shopify-section-group-header-group');
      var top = (header ? header.getBoundingClientRect().bottom : 0) + 12;
      var bottom = (vv ? vv.offsetTop + vv.height : window.innerHeight) - 12;
      var rect = field.getBoundingClientRect();
      if (rect.top >= top && rect.bottom <= bottom) return;
      window.scrollBy({ top: rect.top - top - Math.max(0, (bottom - top - rect.height) / 3), behavior: 'smooth' });
    }

    form.addEventListener('focusin', function (event) {
      var field = event.target;
      if (!field.matches('.pc-input, .pc-select')) return;
      var vv = window.visualViewport;
      var done = false;
      var run = function () {
        if (done) return;
        done = true;
        if (vv) vv.removeEventListener('resize', run);
        if (document.activeElement === field) reveal(field);
      };
      if (vv) vv.addEventListener('resize', run);
      window.setTimeout(run, 350);
    });

    form.addEventListener('keydown', function (event) {
      var target = event.target;
      if (event.key !== 'Enter' || !target.matches('input[type="number"]')) return;
      var controls = Array.prototype.filter.call(form.querySelectorAll('input:not([type="hidden"]), select, button'), function (c) {
        if (c.disabled) return false;
        if (c.type !== 'radio') return true;
        var group = form.querySelector('input[name="' + c.name + '"]:checked') || form.querySelector('input[name="' + c.name + '"]');
        return c === group;
      });
      var next = controls[controls.indexOf(target) + 1];
      if (!next || next.type === 'submit') return;
      event.preventDefault();
      next.focus({ preventScroll: true });
      reveal(next.closest('.pc-choice') || next);
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var input = read();
      var result = compute(input, config);
      var id = ++run;
      if (result.status === 'invalid') {
        render(result, input);
        var firstInvalid = form.querySelector('[aria-invalid="true"]');
        if (firstInvalid) firstInvalid.focus();
        return;
      }
      showErrors({});
      showLoading(id, function () {
        render(result, input);
        submitted = true;
        publish(result, input);
        output.classList.add('is-in');
        output.focus({ preventScroll: true });
      });
    });

    function choiceLabel(name) {
      var control = form.querySelector('select[name="' + name + '"]');
      if (control) return control.options[control.selectedIndex] ? control.options[control.selectedIndex].text.trim() : '';
      var checked = form.querySelector('input[name="' + name + '"]:checked');
      var text = checked && checked.closest('label') && checked.closest('label').querySelector('.pc-choice-text');
      return text ? text.firstChild.textContent.trim() : '';
    }

    // Reason: cart attributes travel to the order, so the coach reads the result before the call.
    // Empty values clear attributes left by a previous calculation.
    function callAttributes(result, input) {
      var attributes = {
        'Calculateur': 'Protéines',
        'Âge': input.age || '',
        'Taille (cm)': input.height || '',
        'Poids (kg)': input.weight ? String(input.weight).replace('.', ',') : '',
        'Profil': choiceLabel('profile'),
        'Régime': choiceLabel('diet'),
        'Repas': choiceLabel('meals'),
        'Plancher (g/jour)': String(result.floor),
        'Cible (g/jour)': String(result.target),
        'Par repas (g)': String(result.perMeal),
        'Poids de référence (kg)': result.adjusted ? String(result.refWeight) : '',
        'Message': refs.callMessage ? refs.callMessage.value.trim() : ''
      };
      // Reason: clear what the deficit calculator may have left in the same cart.
      ['Sexe', 'Cible (kcal/jour)', 'Maintien (kcal/jour)', 'Déficit (kcal/jour)', 'Plancher (kcal/jour)', 'Rythme choisi', 'Rythme retenu (kg/sem.)', 'Activité', 'Limite appliquée', 'Objectif (kg à perdre)'].forEach(function (key) {
        attributes[key] = '';
      });
      return attributes;
    }

    var cta = output.querySelector('[data-pc-cta]');
    if (cta) {
      cta.addEventListener('click', function () {
        checkoutAfterBooking();
        if (last) saveAttributes(callAttributes(last.result, last.input)).catch(function () {});
        var analytics = window.Shopify && window.Shopify.analytics;
        if (!analytics || typeof analytics.publish !== 'function') return;
        try {
          analytics.publish('protein_calc_cta_click', { goal: read().profile });
        } catch (e) {}
      });
    }

    form.addEventListener('change', function () {
      if (!submitted || output.hasAttribute('aria-busy')) return;
      var input = read();
      render(compute(input, config), input);
    });
  }

  var api = { compute: compute, buildDay: buildDay, validate: validate };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.OwlProteinCalculator = api;

  if (typeof document !== 'undefined') {
    var boot = function () {
      Array.prototype.forEach.call(document.querySelectorAll('[data-protein-calculator]'), init);
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }
})(typeof window !== 'undefined' ? window : globalThis);
